import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	faBan,
	faChevronUp,
	faDownload,
	faPlus,
	faSparkles,
	faXmark,
} from "@fortawesome/pro-regular-svg-icons";

import useLocalStorageState from "use-local-storage-state";

import { Icon } from "@/components";
import { Spinner } from "@/components/Spinner/Spinner";
import { TablePagination } from "@/components/TablePagination/TablePagination";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import { composedPathContains } from "@/utils/getComposedEventTarget";

import { SelectListPopover } from "../../Contacts/components/SelectListPopover";

type ActivePopover = "addTo" | null;

interface LinkedinConnectionsActionBarProps {
	selectedCount: number;
	overallCount?: number;
	enrichEnabled: boolean;
	isAddToListLoading: boolean;
	isEnrichLoading: boolean;
	isBlacklistLoading: boolean;
	onAddToList: (list: { id: number; name: string }) => void;
	onCreateAndAddToList: (name: string) => void;
	onEnrich: () => void;
	onBlacklist: () => void;
	onExport: () => void;
	clearSelection?: () => void;
	currentPage?: number;
	totalPages?: number;
	handleNavigateToPage?: (page: number) => void;
	handleChangeRowsPerPage?: (rowsPerPage: number) => void;
	limit?: number;
}

export const LinkedinConnectionsActionBar: FunctionComponent<LinkedinConnectionsActionBarProps> = ({
	selectedCount,
	overallCount,
	enrichEnabled,
	isAddToListLoading,
	isEnrichLoading,
	isBlacklistLoading,
	onAddToList,
	onCreateAndAddToList,
	onEnrich,
	onBlacklist,
	onExport,
	clearSelection,
	currentPage,
	totalPages,
	handleNavigateToPage,
	handleChangeRowsPerPage,
	limit,
}) => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`account.${key}`), [t]);

	const [openPopover, setOpenPopover] = useState<ActivePopover>(null);
	const popoverRef = useRef<HTMLDivElement>(null);

	const [isSidebarOpen] = useLocalStorageState("isSidebarOpen", {
		defaultValue: false,
	});

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (popoverRef.current && !composedPathContains(popoverRef.current, e)) {
				setOpenPopover(null);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	useEffect(() => {
		const handleResize = () => {
			const footerEl = document.querySelector("#connections-action-bar") as HTMLElement;
			const floatingEl = document.getElementById("connections-floating-bulk-bar");
			const outletEl = document.getElementById("outlet-container");

			if (outletEl && footerEl) {
				footerEl.style.setProperty("width", `${outletEl.offsetWidth}px`, "important");
			}

			if (outletEl && floatingEl) {
				const rect = outletEl.getBoundingClientRect();
				const center = rect.left + rect.width / 2;

				floatingEl.style.setProperty("left", `${center}px`, "important");
				floatingEl.style.setProperty("max-width", `${rect.width - 16}px`, "important");
			}
		};

		handleResize();

		const outletEl = document.getElementById("outlet-container");
		let resizeObs: ResizeObserver | null = null;

		if (outletEl) {
			resizeObs = new ResizeObserver(handleResize);
			resizeObs.observe(outletEl);
		}

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
			resizeObs?.disconnect();
		};
	}, [selectedCount, isSidebarOpen]);

	return (
		<>
			{selectedCount > 0 && (
				<div
					className="fixed bottom-[54px] left-1/2 -translate-x-1/2 z-150 flex flex-row items-center gap-2 bg-gray-900 text-white rounded-lg pl-4 pr-2 py-2 shadow-lg"
					id="connections-floating-bulk-bar"
				>
					<span className="flex items-center gap-1.5 text-xs font-medium whitespace-nowrap">
						<span className="bg-blue-action text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
							{selectedCount}
						</span>
						selected
					</span>

					{clearSelection && (
						<button
							aria-label="Clear selection"
							className="flex items-center justify-center w-5 h-5 rounded text-white hover:bg-white/10"
							type="button"
							onClick={clearSelection}
						>
							<Icon className="w-3.5 h-3.5" icon={faXmark} />
						</button>
					)}

					<span className="w-px h-5 bg-white/20 mx-1" />

					<div ref={popoverRef} className="flex flex-row gap-1 items-center">
						{/* Add to */}
						<div className="relative">
							{isAddToListLoading ? (
								<div className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white">
									<Spinner color="#fff" />
									{t("basics.wait")}
								</div>
							) : (
								<button
									className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs text-white hover:bg-white/10 cursor-pointer duration-150 whitespace-nowrap"
									type="button"
									onClick={() => setOpenPopover((p) => (p === "addTo" ? null : "addTo"))}
								>
									<Icon className="w-3.5 h-3.5" icon={faPlus} />
									{ts("myConnections.addTo")}
									<Icon className="w-2.5 h-2.5" icon={faChevronUp} />
								</button>
							)}
							{openPopover === "addTo" && (
								<div className="absolute bottom-full mb-2 left-0">
									<SelectListPopover
										isLoading={isAddToListLoading}
										onCreateNew={(name) => {
											onCreateAndAddToList(name);
											setOpenPopover(null);
										}}
										onSelectList={(list) => {
											onAddToList(list);
											setOpenPopover(null);
										}}
									/>
								</div>
							)}
						</div>

						{/* Enrich */}
						<Tooltip content={ts("myConnections.enrichDisabledTooltip")} disabled={enrichEnabled}>
							{isEnrichLoading ? (
								<div className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white">
									<Spinner color="#fff" />
									{t("basics.wait")}
								</div>
							) : (
								<button
									className={
										enrichEnabled
											? "flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs text-white hover:bg-white/10 cursor-pointer duration-150 whitespace-nowrap"
											: "flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs text-white/40 cursor-not-allowed whitespace-nowrap"
									}
									disabled={!enrichEnabled}
									type="button"
									onClick={enrichEnabled ? onEnrich : undefined}
								>
									<Icon className="w-3.5 h-3.5" icon={faSparkles} />
									{ts("myConnections.enrich")}
								</button>
							)}
						</Tooltip>

						{/* Blacklist */}
						{isBlacklistLoading ? (
							<div className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white">
								<Spinner color="#fff" />
								{t("basics.wait")}
							</div>
						) : (
							<button
								className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs text-white hover:bg-white/10 cursor-pointer duration-150 whitespace-nowrap"
								type="button"
								onClick={onBlacklist}
							>
								<Icon className="w-3.5 h-3.5" icon={faBan} />
								{ts("myConnections.blacklist")}
							</button>
						)}

						{/* Export (stub) */}
						<button
							className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs text-white/40 cursor-not-allowed whitespace-nowrap"
							disabled
							type="button"
							onClick={onExport}
						>
							<Icon className="w-3.5 h-3.5" icon={faDownload} />
							{ts("myConnections.export")}
						</button>
					</div>
				</div>
			)}

			{/* Fixed footer bar */}
			<div
				className="fixed flex flex-row items-center h-[38px] border-t border-border-light bottom-0 bg-white z-40 px-3 gap-3"
				id="connections-action-bar"
			>
				<button
					className="flex items-center gap-1.5 text-xs text-gray-400 cursor-not-allowed"
					disabled
					type="button"
				>
					<Icon className="w-3.5 h-3.5" icon={faDownload} />
					{ts("myConnections.export")}
				</button>

				<span className="ml-auto text-xs text-gray-500">
					{ts("myConnections.rowsOf")
						.replace("{{shown}}", String(Math.min(limit ?? 25, overallCount ?? 0)))
						.replace("{{total}}", String(overallCount ?? 0))}
				</span>

				<TablePagination
					currentPage={currentPage ?? 1}
					handleChangeRowsPerPage={handleChangeRowsPerPage}
					handleNavigateToPage={handleNavigateToPage ?? (() => {})}
					limit={limit ?? 25}
					maxPage={totalPages ?? 1}
				/>
			</div>
		</>
	);
};
